import { useCallback, useEffect, useRef, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  X, Paperclip, Download, Eye, PaperPlaneRight, ClockCounterClockwise, PencilSimple, Trash, Check,
} from "@phosphor-icons/react";

const ATTACHMENT_SECTIONS = [
  { id: "origination_closing",       label: "Origination closing" },
  { id: "appraisal",                 label: "Appraisal" },
  { id: "payoff_statement",          label: "Payoff statement" },
  { id: "loan_termination_closing",  label: "Loan termination closing statement" },
  { id: "extension_documents",       label: "Extension documents" },
  { id: "entity_documents",          label: "Entity documents" },
  { id: "insurance_docs_lender",     label: "Insurance Docs To Lender (If any)" },
  { id: "audit_documents",           label: "Audit Documents" },
  // ``versioned: false`` renders a flat list — no "current version" /
  // "previous versions" chrome; every uploaded file is shown in one list.
  { id: "additional_documents",      label: "Additional Documents", versioned: false },
];

const LOAN_FIELDS_FOR_AUTOCOMPLETE = ["collateral", "loan_type_class", "lender", "borrower", "rate_type"];

const fmtMoney = (n) => {
  if (n == null || n === "") return "—";
  const v = Number(n);
  if (!isFinite(v)) return "—";
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};
const fmtPct = (n) => {
  if (n == null || n === "") return "—";
  const v = Number(n);
  if (!isFinite(v)) return "—";
  return `${v.toFixed(3).replace(/\.?0+$/, "")}%`;
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};
const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })} · ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
};

export default function LoanModal({ loan, onClose, onChange, customCols = [] }) {
  const { user } = useAuth();
  const [form, setForm] = useState(loan || {});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [log, setLog] = useState([]);
  const [notes, setNotes] = useState([]);
  const [noteBody, setNoteBody] = useState("");
  const [postingNote, setPostingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteBody, setEditNoteBody] = useState("");
  const [savingNoteEdit, setSavingNoteEdit] = useState(false);
  const [attachments, setAttachments] = useState({}); // { section: [items] }
  const [uploading, setUploading] = useState(null); // section id
  const [expandedHistory, setExpandedHistory] = useState({}); // { section: bool }
  const [suggestions, setSuggestions] = useState({});
  const fileInputRefs = useRef({});

  useEffect(() => {
    setForm(loan || {});
    setDirty(false);
  }, [loan?.id]);

  const loadAll = useCallback(async (lid) => {
    try {
      const [l, n, a] = await Promise.all([
        api.get(`/loans/${lid}/log`),
        api.get(`/loans/${lid}/notes`),
        api.get(`/loans/${lid}/attachments`),
      ]);
      setLog(l.data || []);
      setNotes(n.data || []);
      setAttachments(a.data || {});
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    }
  }, []);

  useEffect(() => {
    if (loan?.id) loadAll(loan.id);
  }, [loan?.id, loadAll]);

  // Autocomplete suggestions.
  useEffect(() => {
    if (!loan?.id) return;
    (async () => {
      const results = await Promise.all(LOAN_FIELDS_FOR_AUTOCOMPLETE.map((f) =>
        api.get(`/loans/field-values?field=${f}`).then((r) => [f, r.data]).catch(() => [f, []])
      ));
      setSuggestions(Object.fromEntries(results));
    })();
  }, [loan?.id]);

  if (!loan) return null;

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Only submit fields the API expects.
      delete payload.id;
      delete payload.loan_id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.created_by_id;
      delete payload.created_by_name;
      // Keep stored term_months in sync with the computed value so exports
      // and reports stay accurate when the computed term is available.
      if (payload.loan_start_date && payload.maturity_date) {
        const s = new Date(String(payload.loan_start_date).slice(0, 10) + "T00:00:00");
        const m = new Date(String(payload.maturity_date).slice(0, 10) + "T00:00:00");
        if (!Number.isNaN(s.getTime()) && !Number.isNaN(m.getTime()) && m >= s) {
          const years = m.getFullYear() - s.getFullYear();
          const months = m.getMonth() - s.getMonth();
          const dayDelta = m.getDate() - s.getDate();
          let base = years * 12 + months + (dayDelta < 0 ? -1 : 0);
          const anchor = new Date(s); anchor.setMonth(anchor.getMonth() + base);
          const frac = (m - anchor) / (24 * 3600 * 1000 * 30.4375);
          payload.term_months = Math.round((base + frac) * 10) / 10;
        }
      }
      const { data } = await api.put(`/loans/${loan.id}`, payload);
      toast.success("Loan updated");
      setDirty(false);
      onChange && onChange(data);
      loadAll(loan.id);
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    } finally {
      setSaving(false);
    }
  };

  const postNote = async () => {
    const body = noteBody.trim();
    if (!body) return;
    setPostingNote(true);
    try {
      const { data } = await api.post(`/loans/${loan.id}/notes`, { body });
      setNotes((prev) => [data, ...prev]);
      setNoteBody("");
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    } finally { setPostingNote(false); }
  };

  const deleteNote = async (n) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/loans/${loan.id}/notes/${n.id}`);
      setNotes((prev) => prev.filter((x) => x.id !== n.id));
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    }
  };

  const startEditNote = (n) => { setEditingNoteId(n.id); setEditNoteBody(n.body || ""); };
  const cancelEditNote = () => { setEditingNoteId(null); setEditNoteBody(""); };
  const saveEditNote = async (n) => {
    const body = editNoteBody.trim();
    if (!body) { toast.error("Note cannot be empty"); return; }
    if (body === (n.body || "").trim()) { cancelEditNote(); return; }
    setSavingNoteEdit(true);
    try {
      const { data } = await api.put(`/loans/${loan.id}/notes/${n.id}`, { body });
      setNotes((prev) => prev.map((x) => x.id === n.id ? data : x));
      cancelEditNote();
      toast.success("Note updated");
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
    finally { setSavingNoteEdit(false); }
  };

  const uploadTo = async (sectionId, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024 * 1024) { toast.error("File exceeds 5 GB"); return; }
    setUploading(sectionId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/loans/${loan.id}/attachments?section=${sectionId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      loadAll(loan.id);
      toast.success("Uploaded");
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    } finally { setUploading(null); }
  };

  const deleteAttachment = async (fileId) => {
    if (!window.confirm("Delete this attachment (this specific version)?")) return;
    try {
      await api.delete(`/loans/${loan.id}/attachments/${fileId}`);
      loadAll(loan.id);
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    }
  };

  const attachUrl = (fileId, dl = false) =>
    `${process.env.REACT_APP_BACKEND_URL}/api/loans/${loan.id}/attachments/${fileId}${dl ? "?download=true" : ""}`;

  return (
    <Dialog open={!!loan} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[1400px] w-[96vw] p-0 overflow-hidden" data-testid="loan-modal">
        {/* Header */}
        <div className="px-5 py-3 bg-gradient-to-r from-[#1f64c2] to-[#327de3] text-white flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest opacity-80">Loan</div>
            <div className="text-lg font-bold">#{loan.loan_id} · {loan.collateral || "(no collateral set)"}</div>
          </div>
          {dirty && (
            <Button size="sm" onClick={save} disabled={saving} className="bg-white text-[#1f64c2] hover:bg-slate-100" data-testid="loan-modal-save">
              <Check size={14} className="mr-1" /> {saving ? "Saving…" : "Save changes"}
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 p-3 max-h-[80vh] overflow-y-auto">
          {/* LEFT: mini KPI grid + fields */}
          <div className="lg:col-span-2 space-y-3">
            {(() => {
              const num = (v) => (v == null || v === "" ? 0 : Number(v) || 0);
              const equity = num(loan.current_value) - num(loan.principal);
              // Cash flow uses the user-entered Monthly Payment (no synthetic interest calc).
              const cashFlow = num(loan.monthly_revenue) - num(loan.monthly_expense) - num(loan.monthly_tax) - num(loan.monthly_payment);
              return (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  <Mini label="Loan ID" value={`#${loan.loan_id}`} />
                  <Mini label="Principal" value={fmtMoney(loan.principal)} />
                  <Mini label="Interest Rate" value={fmtPct(loan.interest_rate)} />
                  <Mini label="Monthly Payment" value={fmtMoney(loan.monthly_payment)} />
                  <Mini label="Current Value" value={fmtMoney(loan.current_value)} />
                  <Mini label="Maturity" value={fmtDate(loan.maturity_date)} />
                  <Mini label="Equity Value" value={fmtMoney(equity)} tone={equity >= 0 ? "positive" : "negative"} />
                  <Mini label="Monthly Revenue" value={fmtMoney(loan.monthly_revenue)} />
                  <Mini label="Monthly Expense" value={fmtMoney(loan.monthly_expense)} />
                  <Mini label="Monthly Taxes" value={fmtMoney(loan.monthly_tax)} />
                  <div className="col-span-2 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1.5" data-testid="loan-modal-cashflow">
                    <div className="text-[9px] uppercase tracking-widest text-emerald-700 font-bold">Est. Monthly Cash Flow</div>
                    <div className={`text-sm font-bold leading-tight ${cashFlow >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fmtMoney(cashFlow)}</div>
                  </div>
                </div>
              );
            })()}

            {/* Editable field grid */}
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Loan details (editable)</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <FieldText label="Collateral" value={form.collateral} onChange={(v) => setField("collateral", v)} testId="loan-modal-collateral" suggestions={suggestions.collateral} />
                <FieldText label="Loan Type" value={form.loan_type_class} onChange={(v) => setField("loan_type_class", v)} testId="loan-modal-loan-type" suggestions={suggestions.loan_type_class} />
                <FieldText label="Lender" value={form.lender} onChange={(v) => setField("lender", v)} testId="loan-modal-lender" suggestions={suggestions.lender} />
                <FieldText label="Borrower" value={form.borrower} onChange={(v) => setField("borrower", v)} testId="loan-modal-borrower" suggestions={suggestions.borrower} />
                <FieldNumber label="Loan Principal" value={form.principal} onChange={(v) => setField("principal", v)} testId="loan-modal-principal" />
                <FieldNumber label="Monthly Payment" value={form.monthly_payment} onChange={(v) => setField("monthly_payment", v)} testId="loan-modal-monthly" />
                <FieldNumber label="Construction Holdback Amt" value={form.construction_holdback} onChange={(v) => setField("construction_holdback", v)} testId="loan-modal-holdback" />
                <FieldNumber label="Origination Points (%)" value={form.origination_points} onChange={(v) => setField("origination_points", v)} testId="loan-modal-orig-points" step="0.001" />
                <FieldNumber label="Extension Points (%)" value={form.extension_points} onChange={(v) => setField("extension_points", v)} testId="loan-modal-ext-points" step="0.001" />
                <FieldNumber label="Release Amount" value={form.release_amount} onChange={(v) => setField("release_amount", v)} testId="loan-modal-release" />
                <FieldNumber label="Sale Amount" value={form.sale_amount} onChange={(v) => setField("sale_amount", v)} testId="loan-modal-sale" />
                <FieldNumber label="Purchase Price" value={form.purchase_price} onChange={(v) => setField("purchase_price", v)} testId="loan-modal-purchase-price" />
                <FieldNumber label="Current Value" value={form.current_value} onChange={(v) => setField("current_value", v)} testId="loan-modal-current-value" />
                <FieldNumber label="ARV" value={form.arv} onChange={(v) => setField("arv", v)} testId="loan-modal-arv" />
                <FieldComputedTerm label="Term (months)" start={form.loan_start_date} maturity={form.maturity_date} testId="loan-modal-term" />
                <FieldText label="Type" value={form.rate_type} onChange={(v) => setField("rate_type", v)} testId="loan-modal-rate-type" suggestions={suggestions.rate_type} />
                <FieldDate label="Loan Start Date" value={form.loan_start_date} onChange={(v) => setField("loan_start_date", v)} testId="loan-modal-loan-start" />
                <FieldDate label="Maturity Date" value={form.maturity_date} onChange={(v) => setField("maturity_date", v)} testId="loan-modal-maturity" />
                <FieldNumber label="Interest Rate (%)" value={form.interest_rate} onChange={(v) => setField("interest_rate", v)} testId="loan-modal-rate" step="0.001" />
                <FieldDate label="Step Up Date" value={form.step_up_date} onChange={(v) => setField("step_up_date", v)} testId="loan-modal-step-date" />
                <FieldNumber label="Step Up Rate (%)" value={form.step_up_rate} onChange={(v) => setField("step_up_rate", v)} testId="loan-modal-step-rate" step="0.001" />
                <FieldNumber label="Interest Reserve" value={form.interest_reserve} onChange={(v) => setField("interest_reserve", v)} testId="loan-modal-interest-reserve" />
                <FieldNumber label="Balloon Payment" value={form.balloon_payment} onChange={(v) => setField("balloon_payment", v)} testId="loan-modal-balloon" />
                <FieldNumber label="Monthly Revenue" value={form.monthly_revenue} onChange={(v) => setField("monthly_revenue", v)} testId="loan-modal-monthly-rev" />
                <FieldNumber label="Monthly Expense" value={form.monthly_expense} onChange={(v) => setField("monthly_expense", v)} testId="loan-modal-monthly-exp" />
                <FieldNumber label="Monthly Tax" value={form.monthly_tax} onChange={(v) => setField("monthly_tax", v)} testId="loan-modal-monthly-tax" />
                {customCols.map((c) => (
                  <CustomField
                    key={c.id}
                    col={c}
                    value={(form.custom_fields || {})[c.key]}
                    onChange={(v) => {
                      setForm((f) => ({ ...f, custom_fields: { ...(f.custom_fields || {}), [c.key]: v } }));
                      setDirty(true);
                    }}
                    testId={`loan-modal-custom-${c.key}`}
                  />
                ))}
              </div>
            </div>

            {/* Attachments: 5 sections with version history */}
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Documents</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {ATTACHMENT_SECTIONS.map((s) => {
                  const versions = attachments[s.id] || [];
                  const current = versions[0]; // newest (backend already sorts by uploaded_at desc)
                  const previous = versions.slice(1);
                  const historyOpen = !!expandedHistory[s.id];
                  const isFlat = s.versioned === false;
                  return (
                    <div key={s.id} className="rounded-md border border-slate-200 bg-slate-50/50 p-2" data-testid={`loan-attach-section-${s.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-[11px] font-bold text-slate-700">{s.label}</div>
                        <button
                          type="button"
                          onClick={() => fileInputRefs.current[s.id]?.click()}
                          className="text-[10px] font-semibold text-[#1f64c2] hover:text-[#327de3] inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-slate-200 bg-white hover:border-[#327de3]"
                          disabled={uploading === s.id}
                          data-testid={`loan-attach-upload-${s.id}`}
                        >
                          <Paperclip size={10} /> {uploading === s.id ? "Uploading…" : "Add file"}
                        </button>
                        <input
                          ref={(el) => { fileInputRefs.current[s.id] = el; }}
                          type="file"
                          className="hidden"
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadTo(s.id, f); e.target.value = ""; }}
                        />
                      </div>
                      {isFlat ? (
                        versions.length === 0 ? (
                          <div className="text-[10px] text-slate-400 italic">No files uploaded.</div>
                        ) : (
                          <div className="space-y-0.5">
                            {versions.map((v) => (
                              <AttachmentRow key={v.id} v={v} attachUrl={attachUrl} onDelete={() => deleteAttachment(v.id)} hideVersion />
                            ))}
                          </div>
                        )
                      ) : !current ? (
                        <div className="text-[10px] text-slate-400 italic">No files uploaded.</div>
                      ) : (
                        <>
                          <div className="text-[9px] font-bold uppercase tracking-widest text-emerald-700 mb-0.5">Current version</div>
                          <AttachmentRow v={current} attachUrl={attachUrl} onDelete={() => deleteAttachment(current.id)} />
                          {previous.length > 0 && (
                            <div className="mt-1">
                              <button
                                type="button"
                                onClick={() => setExpandedHistory((prev) => ({ ...prev, [s.id]: !prev[s.id] }))}
                                className="text-[9px] uppercase tracking-widest font-semibold text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
                                data-testid={`loan-attach-history-toggle-${s.id}`}
                              >
                                <span className={`inline-block transition-transform ${historyOpen ? "rotate-90" : ""}`}>▸</span>
                                Previous versions ({previous.length})
                              </button>
                              {historyOpen && (
                                <div className="mt-1 space-y-0.5 pl-3 border-l border-slate-200">
                                  {previous.map((v) => (
                                    <AttachmentRow key={v.id} v={v} attachUrl={attachUrl} onDelete={() => deleteAttachment(v.id)} />
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* RIGHT: change log + notes */}
          <div className="space-y-3">
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2 flex items-center gap-1">
                <ClockCounterClockwise size={11} /> Change log
              </div>
              <div className="space-y-1.5 overflow-y-auto pr-1" style={{ maxHeight: "calc(80vh / 3)" }}>
                {log.length === 0 && <div className="text-[10px] text-slate-400 italic">No changes yet.</div>}
                {log.map((l) => (
                  <div key={l.id} className="text-[10px] border-l-2 border-slate-200 pl-2" data-testid={`loan-log-${l.id}`}>
                    <div className="text-slate-500">{fmtTime(l.changed_at)} · <span className="font-semibold text-slate-700">{l.changed_by_name}</span></div>
                    <div className="text-slate-800">
                      {l.field === "__created__" ? (
                        <>Created loan #{l.new_value}</>
                      ) : l.field.startsWith("attachment.") ? (
                        l.new_value ? (
                          <><span className="font-semibold">+ document</span> {l.field.replace("attachment.", "")}: {l.new_value.filename} <span className="text-slate-400">(v{l.new_value.version})</span></>
                        ) : (
                          <><span className="font-semibold text-rose-600">− document</span> {l.field.replace("attachment.", "")}: {l.old_value?.filename} <span className="text-slate-400">(v{l.old_value?.version})</span></>
                        )
                      ) : (
                        <>Changed <span className="font-semibold">{l.field}</span> from <code className="text-slate-500">{formatVal(l.old_value)}</code> to <code className="text-[#1f64c2]">{formatVal(l.new_value)}</code></>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Notes timeline</div>
              <div className="flex gap-1.5 mb-2">
                <Textarea
                  value={noteBody}
                  onChange={(e) => setNoteBody(e.target.value)}
                  placeholder="Add a note about this loan…"
                  rows={2}
                  className="resize-none text-xs"
                  data-testid="loan-note-input"
                />
                <Button size="sm" onClick={postNote} disabled={postingNote || !noteBody.trim()} className="bg-[#327de3] hover:bg-[#1f64c2] self-end" data-testid="loan-note-post">
                  <PaperPlaneRight size={12} />
                </Button>
              </div>
              <div className="space-y-1.5 overflow-y-auto pr-1" style={{ maxHeight: "calc(80vh * 2 / 3)" }}>
                {notes.length === 0 && <div className="text-[10px] text-slate-400 italic">No notes yet.</div>}
                {notes.map((n) => {
                  const mine = n.author_id === user?.id;
                  const canEdit = mine || user?.is_super_admin;
                  const isEditing = editingNoteId === n.id;
                  const wasEdited = !!n.edited_at && n.edited_at !== n.created_at;
                  return (
                    <div key={n.id} className="text-[11px] rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5" data-testid={`loan-note-${n.id}`}>
                      <div className="flex items-center gap-1 text-[10px] text-slate-500">
                        <span className="font-semibold text-slate-700">{n.author_name}</span> · {fmtTime(n.created_at)}
                        {wasEdited && (
                          <span className="italic text-slate-400" title={`Edited ${fmtTime(n.edited_at)}${n.last_edited_by_name ? ` by ${n.last_edited_by_name}` : ""}`}>
                            · edited {fmtTime(n.edited_at)}
                          </span>
                        )}
                        {canEdit && !isEditing && (
                          <button onClick={() => startEditNote(n)} className="ml-auto text-slate-500 hover:text-[#1f64c2]" title="Edit note" data-testid={`loan-note-edit-${n.id}`}>
                            <PencilSimple size={10} />
                          </button>
                        )}
                        {canEdit && !isEditing && (
                          <button onClick={() => deleteNote(n)} className="text-rose-500 hover:text-rose-700" title="Delete">
                            <Trash size={10} />
                          </button>
                        )}
                      </div>
                      {isEditing ? (
                        <div className="mt-1 space-y-1">
                          <Textarea value={editNoteBody} onChange={(e) => setEditNoteBody(e.target.value)} rows={2}
                            className="resize-none text-xs" autoFocus data-testid={`loan-note-edit-input-${n.id}`} />
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={cancelEditNote} disabled={savingNoteEdit} className="h-6 px-2 text-[10px]" data-testid={`loan-note-edit-cancel-${n.id}`}>
                              <X size={10} className="mr-0.5" /> Cancel
                            </Button>
                            <Button size="sm" onClick={() => saveEditNote(n)} disabled={savingNoteEdit || !editNoteBody.trim()}
                              className="h-6 px-2 text-[10px] bg-[#327de3] hover:bg-[#1f64c2]" data-testid={`loan-note-edit-save-${n.id}`}>
                              <Check size={10} className="mr-0.5" /> {savingNoteEdit ? "Saving…" : "Save"}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="whitespace-pre-wrap text-slate-800">{n.body}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AttachmentRow({ v, attachUrl, onDelete, hideVersion }) {
  return (
    <div className="flex items-center gap-1 text-[10px] bg-white border border-slate-200 rounded px-1.5 py-0.5" data-testid={`loan-attach-item-${v.id}`}>
      {!hideVersion && <span className="font-semibold text-slate-500">v{v.version}</span>}
      <span className="truncate flex-1" title={v.filename}>{v.filename}</span>
      <a href={attachUrl(v.id)} target="_blank" rel="noreferrer" className="text-[#1f64c2] hover:text-[#327de3]" title="View"><Eye size={10} /></a>
      <a href={attachUrl(v.id, true)} className="text-[#1f64c2] hover:text-[#327de3]" title="Download"><Download size={10} /></a>
      <button onClick={onDelete} className="text-rose-500 hover:text-rose-700" title="Delete"><Trash size={10} /></button>
    </div>
  );
}

function formatVal(v) {
  if (v == null || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function Mini({ label, value, tone }) {
  const cls =
    tone === "positive" ? "border-emerald-200 bg-emerald-50" :
    tone === "negative" ? "border-rose-200 bg-rose-50" :
    "border-slate-200 bg-slate-50";
  const valCls =
    tone === "positive" ? "text-emerald-700" :
    tone === "negative" ? "text-rose-700" :
    "text-slate-900";
  return (
    <div className={`rounded-md border ${cls} px-2 py-1.5`}>
      <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`text-sm font-bold ${valCls} leading-tight`}>{value}</div>
    </div>
  );
}

function FieldText({ label, value, onChange, testId, suggestions }) {
  const listId = suggestions?.length ? `dl-${testId}` : undefined;
  return (
    <label className="block text-[10px]">
      <span className="uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <Input value={value ?? ""} list={listId} onChange={(e) => onChange(e.target.value)} className="text-xs h-7 mt-0.5" data-testid={testId} autoComplete="off" />
      {listId && <datalist id={listId}>{suggestions.map((s) => <option key={s} value={s} />)}</datalist>}
    </label>
  );
}

function FieldNumber({ label, value, onChange, testId, step }) {
  return (
    <label className="block text-[10px]">
      <span className="uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <Input type="number" step={step || "any"} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} className="text-xs h-7 mt-0.5" data-testid={testId} />
    </label>
  );
}

function FieldDate({ label, value, onChange, testId }) {
  return (
    <label className="block text-[10px]">
      <span className="uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <Input type="date" value={value ? String(value).slice(0, 10) : ""} onChange={(e) => onChange(e.target.value || null)} className="text-xs h-7 mt-0.5" data-testid={testId} />
    </label>
  );
}

function FieldSelect({ label, value, options, onChange, testId }) {
  return (
    <label className="block text-[10px]">
      <span className="uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value || null)} className="text-xs h-7 mt-0.5 w-full border border-slate-300 rounded-md px-2 bg-white" data-testid={testId}>
        <option value=""></option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

/** Read-only Term (months) — auto-computed from Loan Start Date → Maturity Date. */
function FieldComputedTerm({ label, start, maturity, testId }) {
  let computed = "";
  if (start && maturity) {
    const s = new Date(String(start).slice(0, 10) + "T00:00:00");
    const m = new Date(String(maturity).slice(0, 10) + "T00:00:00");
    if (!Number.isNaN(s.getTime()) && !Number.isNaN(m.getTime()) && m >= s) {
      const years = m.getFullYear() - s.getFullYear();
      const months = m.getMonth() - s.getMonth();
      const dayDelta = m.getDate() - s.getDate();
      let base = years * 12 + months + (dayDelta < 0 ? -1 : 0);
      const anchor = new Date(s); anchor.setMonth(anchor.getMonth() + base);
      const frac = (m - anchor) / (24 * 3600 * 1000 * 30.4375);
      computed = Math.round((base + frac) * 10) / 10;
    }
  }
  return (
    <label className="block text-[10px]">
      <span className="uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <div className="text-xs h-7 mt-0.5 w-full border border-slate-200 rounded-md px-2 bg-slate-50 flex items-center text-slate-700" data-testid={testId}>
        {computed === "" ? <span className="text-slate-400 italic">auto</span> : computed}
      </div>
    </label>
  );
}

/**
 * Render a single custom column's editor. Type comes from the backend
 * column definition — text / number / money / percent / date.
 */
function CustomField({ col, value, onChange, testId }) {
  const label = col.label || col.key;
  if (col.type === "date") {
    return <FieldDate label={label} value={value} onChange={onChange} testId={testId} />;
  }
  if (col.type === "money" || col.type === "number" || col.type === "percent") {
    const step = col.type === "percent" ? "0.001" : "any";
    return <FieldNumber label={label} value={value} onChange={onChange} testId={testId} step={step} />;
  }
  return <FieldText label={label} value={value} onChange={onChange} testId={testId} />;
}
